/**
 * Supabase Client Configuration - Dynamic Initialization
 * Issue #237 Phase 1: DataService統合のための動的初期化対応
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import logger from '../utils/logger';

// 環境変数から設定値を取得（Vite環境）
let supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL;
let supabaseKey: string | undefined = import.meta.env.VITE_SUPABASE_ANON_KEY;

// 動的初期化のためのキャッシュ
let supabase: SupabaseClient | null = null;
let isSupabaseAvailable = false;
let initializationAttempted = false;

/**
 * Supabaseクライアントを動的に初期化
 */
function initializeSupabase(): SupabaseClient | null {
  if (initializationAttempted && supabase) {
    return supabase;
  }

  initializationAttempted = true;

  // 環境変数を動的に読み込み（Vite環境）
  const dynamicSupabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL;
  const dynamicSupabaseKey: string | undefined = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // トップレベル変数も更新
  supabaseUrl = dynamicSupabaseUrl;
  supabaseKey = dynamicSupabaseKey;

  logger.log('🔧 Dynamic Supabase Initialization:');
  logger.log('URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  logger.log('Key:', supabaseKey ? '✅ Set' : '❌ Missing');
  
  // 本番環境での詳細デバッグ情報
  const environment = typeof window !== 'undefined' ? 'browser' : 'nodejs';
  logger.log(`🌍 Environment: ${environment}`);
  
  if (environment === 'browser') {
    logger.log('🔍 Browser Environment Check:');
    logger.log('- import.meta.env available:', typeof import.meta?.env !== 'undefined');
    if (import.meta?.env) {
      const envKeys = Object.keys(import.meta.env).filter(key => key.startsWith('VITE_SUPABASE'));
      logger.log('- VITE_SUPABASE_* keys found:', envKeys.length > 0 ? envKeys : 'None');
    }
  } else {
    logger.log('🔍 Node.js Environment Check:');
    logger.log('- process.env available:', typeof process?.env !== 'undefined');
    if (process?.env) {
      const envKeys = Object.keys(process.env).filter(key => key.startsWith('VITE_SUPABASE'));
      logger.log('- VITE_SUPABASE_* keys found:', envKeys.length > 0 ? envKeys : 'None');
    }
  }

  try {
    if (supabaseUrl && supabaseKey && !supabaseUrl.includes('your-project-id')) {
      const environment = typeof window !== 'undefined' ? 'browser' : 'nodejs';
      
      supabase = createClient(supabaseUrl, supabaseKey, {
        auth: {
          // 環境に応じた認証設定
          persistSession: environment === 'browser',
          autoRefreshToken: true,
          detectSessionInUrl: environment === 'browser'
        },
        db: {
          schema: 'public'
        },
        realtime: {
          params: {
            eventsPerSecond: 2
          }
        }
      });
      
      isSupabaseAvailable = true;
      logger.log(`✅ Supabase initialized successfully (${environment} environment)`);
      logger.log('🌐 URL:', supabaseUrl);
      
      return supabase;
    } else {
      const missingVars = [];
      if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
      if (!supabaseKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
      if (supabaseUrl && supabaseUrl.includes('your-project-id')) {
        missingVars.push('VITE_SUPABASE_URL (contains placeholder)');
      }
      
      logger.warn('⚠️ Supabase configuration missing/invalid.');
      logger.warn('Missing/Invalid variables:', missingVars.join(', '));
      logger.warn('📋 Fix this by setting environment variables in:');
      logger.warn('  - Local: .env file in prototype/ directory');
      logger.warn('  - Netlify: Site settings > Environment variables');
      logger.warn('  - Vercel: Project settings > Environment Variables');
      
      supabase = null;
      isSupabaseAvailable = false;
      return null;
    }
  } catch (error) {
    logger.warn('⚠️ Supabase initialization failed:', error);
    supabase = null;
    isSupabaseAvailable = false;
    return null;
  }
}

/**
 * 初期化状態をリセットして強制再初期化を可能にする
 * テスト環境などで環境変数の動的読み込み後に使用
 */
export function resetSupabaseInitialization(): void {
  logger.log('🔄 Resetting Supabase initialization state...');
  supabase = null;
  isSupabaseAvailable = false;
  initializationAttempted = false;
  supabaseUrl = undefined;
  supabaseKey = undefined;
}

/**
 * Supabaseクライアントを取得（遅延初期化）
 */
export function getSupabase(): SupabaseClient | null {
  if (!supabase && !initializationAttempted) {
    return initializeSupabase();
  }
  return supabase;
}

/**
 * Supabaseの利用可否を確認（遅延初期化）
 */
export function getSupabaseAvailability(): boolean {
  if (!initializationAttempted) {
    initializeSupabase();
  }
  return isSupabaseAvailable;
}

// 後方互換性のためのエクスポート
export { supabase, isSupabaseAvailable };

/**
 * データベース接続テスト関数
 * Supabase環境が正常に設定されているかを確認
 */
export async function testSupabaseConnection(): Promise<{
  success: boolean;
  message: string;
  error?: unknown;
}> {
  const client = getSupabase();
  
  if (!client) {
    return {
      success: false,
      message: '❌ Supabase client not initialized',
      error: new Error('Supabase client is null')
    };
  }

  try {
    // 1. 基本的な接続テスト（customers テーブルの存在確認）
    const { error } = await client
      .from('customers')
      .select('count')
      .limit(1);

    if (error && !error.message.includes('relation') && !error.message.includes('does not exist')) {
      throw error;
    }

    // 2. 認証状態確認
    const { error: authError } = await client.auth.getSession();
    
    if (authError) {
      logger.warn('⚠️ Auth session check failed:', authError.message);
    }

    return {
      success: true,
      message: '✅ Supabase connection successful',
    };
  } catch (error) {
    logger.error('❌ Supabase connection failed:', error);
    return {
      success: false,
      message: '❌ Supabase connection failed',
      error: error
    };
  }
}

/**
 * データベース準備状況確認
 * Phase 1で作成済みのテーブルが存在するかチェック
 */
export async function checkDatabaseSetup(): Promise<{
  tablesReady: boolean;
  existingTables: string[];
  missingTables: string[];
}> {
  const client = getSupabase();
  
  if (!client) {
    return {
      tablesReady: false,
      existingTables: [],
      missingTables: ['Supabase client not available']
    };
  }

  const expectedTables = ['customers', 'reservations', 'treatments', 'medical_records', 'sequences'];
  const existingTables: string[] = [];
  const missingTables: string[] = [];

  for (const table of expectedTables) {
    try {
      const { error } = await client
        .from(table)
        .select('id')
        .limit(1);

      if (error && error.message.includes('does not exist')) {
        missingTables.push(table);
      } else {
        existingTables.push(table);
      }
    } catch (error) {
      missingTables.push(table);
    }
  }

  return {
    tablesReady: missingTables.length === 0,
    existingTables,
    missingTables
  };
}

/**
 * 開発用: Supabase設定状況レポート
 */
export async function getSupabaseStatus(): Promise<void> {
  logger.log('🔍 Supabase Configuration Status');
  logger.log('================================');
  logger.log('URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  logger.log('API Key:', supabaseKey ? '✅ Set' : '❌ Missing');
  logger.log('Available:', getSupabaseAvailability() ? '✅ Yes' : '❌ No');
  
  if (getSupabaseAvailability()) {
    const connectionTest = await testSupabaseConnection();
    logger.log('Connection:', connectionTest.success ? '✅ Success' : '❌ Failed');
    
    if (connectionTest.success) {
      const dbSetup = await checkDatabaseSetup();
      logger.log('Tables Setup:', dbSetup.tablesReady ? '✅ Complete' : '⏳ In Progress');
      logger.log('Existing Tables:', dbSetup.existingTables);
      if (dbSetup.missingTables.length > 0) {
        logger.log('Missing Tables:', dbSetup.missingTables);
      }
    } else {
      logger.log('Connection Error:', (connectionTest.error as Error)?.message || String(connectionTest.error) || 'Unknown error');
    }
  }
}

// 型定義
export interface SupabaseConnectionStatus {
  isConnected: boolean;
  isAuthenticated: boolean;
  tablesReady: boolean;
  error?: string;
}